// R-274 Phase 7d — "When to export" panel helpers.
// Pure functions; no side-effects. Importable in tests without DOM.

import type { ExportTriggerPolicy } from '@capture/shared';

// ─── PanelState ─────────────────────────────────────────────────────────────
// UI form state shape — separate from the wire-format ExportTriggerPolicy so
// the panel can represent "condition checkbox on but value not yet filled in"
// without producing an invalid policy object.

export interface PanelState {
  mode: 'streaming' | 'accumulate';
  grouping?: 'rolling' | 'batch';
  // Rolling flush conditions — each has an On/Off gate so the checkbox model
  // can hold a value even when the condition is disabled.
  maxCountOn?: boolean;
  maxCount?: number;
  maxAgeOn?: boolean;
  maxAgeMinutes?: number;
  cronOn?: boolean;
  cron?: string;
  timezone?: string;
}

// ─── panelStateToPolicy ──────────────────────────────────────────────────────

/**
 * Convert PanelState → ExportTriggerPolicy.
 * Only the enabled flush conditions are included in the output.
 * The result is always accepted by parseExportTriggerPolicy when
 * validatePanelState returns null.
 */
export function panelStateToPolicy(state: PanelState): ExportTriggerPolicy {
  if (state.mode === 'streaming') {
    return { mode: 'streaming' };
  }

  if (state.grouping === 'batch') {
    return { mode: 'accumulate', grouping: 'batch' };
  }

  // rolling
  const flush: ExportTriggerPolicy['flush'] = {};

  if (state.maxCountOn && state.maxCount !== undefined && state.maxCount > 0) {
    flush.maxCount = state.maxCount;
  }
  if (state.maxAgeOn && state.maxAgeMinutes !== undefined && state.maxAgeMinutes > 0) {
    flush.maxAgeMinutes = state.maxAgeMinutes;
  }
  if (state.cronOn && state.cron && state.cron.trim() !== '') {
    flush.cron = state.cron.trim();
    if (state.timezone && state.timezone.trim() !== '') {
      flush.timezone = state.timezone.trim();
    }
  }

  return { mode: 'accumulate', grouping: 'rolling', flush };
}

// ─── validatePanelState ──────────────────────────────────────────────────────

/**
 * Returns null when the state is valid (policy can be saved), or an
 * error string to display inline. Only rolling mode has non-trivial
 * requirements.
 */
export function validatePanelState(state: PanelState): string | null {
  if (state.mode !== 'accumulate' || state.grouping !== 'rolling') {
    return null;
  }

  // Check positive-int guards first (before the "no condition" check so the
  // operator gets a specific error rather than the generic one).
  if (state.maxCountOn) {
    if (
      state.maxCount === undefined ||
      !Number.isInteger(state.maxCount) ||
      state.maxCount <= 0
    ) {
      return 'Max count must be a positive integer';
    }
  }
  if (state.maxAgeOn) {
    if (
      state.maxAgeMinutes === undefined ||
      !Number.isInteger(state.maxAgeMinutes) ||
      state.maxAgeMinutes <= 0
    ) {
      return 'Max age must be a positive integer (minutes)';
    }
  }

  const anyEnabled = state.maxCountOn || state.maxAgeOn || state.cronOn;
  if (!anyEnabled) {
    return 'Pick at least one flush condition (count, age, or schedule)';
  }

  return null;
}

// ─── describeTriggerPolicy ───────────────────────────────────────────────────

function fmtAge(minutes: number): string {
  return minutes % 60 === 0 ? `${minutes / 60}h` : `${minutes}m`;
}

/**
 * Human-readable one-liner for a stored ExportTriggerPolicy.
 * Used as the echo line in the panel and in the inherit chip.
 */
export function describeTriggerPolicy(policy: ExportTriggerPolicy): string {
  if (policy.mode === 'streaming') {
    return 'Exports each document immediately as it completes the pipeline';
  }

  if (policy.grouping === 'batch') {
    return 'Exports once per ingest batch when all documents are ready';
  }

  // rolling
  const flush = policy.flush ?? {};
  const parts: string[] = [];

  if (flush.maxCount !== undefined) {
    parts.push(`**${flush.maxCount} docs** accumulate`);
  }
  if (flush.maxAgeMinutes !== undefined) {
    parts.push(`the oldest has waited **${fmtAge(flush.maxAgeMinutes)}**`);
  }
  if (flush.cron !== undefined) {
    parts.push(`on schedule **${flush.cron}**`);
  }

  if (parts.length === 0) {
    return 'Accumulates documents (no flush conditions configured)';
  }

  const conditionText = parts.join(', or ');
  return `Fires when ${conditionText} — whichever first`;
}

// ─── normalizeForConstraints ─────────────────────────────────────────────────

/**
 * Coerce a PanelState so it never carries a grouping the server would reject
 * for the current job-type processing mode. When `batchDisabled` is true (the
 * job type is in `flow` mode), batch grouping cannot fire — so any stored or
 * in-flight batch grouping is rewritten to `rolling`.
 *
 * Without this, a policy saved as accumulate+batch while the job type was in
 * batch mode would be re-emitted unchanged after the job type switched to
 * flow, producing a server 400 on the debounced save (the radio being
 * visually disabled does not stop the state from holding `grouping:"batch"`).
 */
export function normalizeForConstraints(
  state: PanelState,
  opts: { batchDisabled: boolean },
): PanelState {
  if (opts.batchDisabled && state.mode === 'accumulate' && state.grouping === 'batch') {
    return { ...state, grouping: 'rolling' };
  }
  return state;
}

// ─── policyToPanelState ──────────────────────────────────────────────────────

/**
 * Convert an ExportTriggerPolicy (or null) → PanelState.
 * Null is treated as "default to streaming" — the panel's initial state when
 * no policy is set.
 */
export function policyToPanelState(policy: ExportTriggerPolicy | null): PanelState {
  if (policy === null || policy.mode === 'streaming') {
    return { mode: 'streaming' };
  }

  if (policy.grouping === 'batch') {
    return { mode: 'accumulate', grouping: 'batch' };
  }

  // rolling
  const flush = policy.flush ?? {};
  return {
    mode: 'accumulate',
    grouping: 'rolling',
    maxCountOn: flush.maxCount !== undefined,
    maxCount: flush.maxCount,
    maxAgeOn: flush.maxAgeMinutes !== undefined,
    maxAgeMinutes: flush.maxAgeMinutes,
    cronOn: flush.cron !== undefined,
    cron: flush.cron,
    timezone: flush.timezone,
  };
}
